import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Pencil, Trash2, X, ShieldCheck, User as UserIcon, Mail, Phone } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import ConfirmationModal from '../components/ConfirmationModal';

const ROLE_OPTIONS = [
  { value: 'user',  label: 'User'  },
  { value: 'admin', label: 'Admin' }
];

const EMPTY_FORM = {
  _id: '',
  name: '',
  email: '',
  password: '',
  phone: '',
  role: 'user',
  isActive: true,
  // NetSapiens dahili (extension). Empty = no VoIP attribution; CDR poller
  // skips this user entirely. Persisted via the dedicated voip endpoint
  // so the user-update path stays focused on identity / role fields.
  extension: ''
};

const TeamMembers = () => {
  const queryClient = useQueryClient();
  const { user: currentUser, organization } = useAuth();
  // VoIP terminology adapts to the active provider — NetSapiens uses
  // numeric extensions ("101"), VoIP.ms uses sub-account names
  // ("545782_user1"). The underlying User.voip.extension field stores
  // either; only the label/placeholder/help text changes here.
  const voipProvider = organization?.voip?.provider || 'netsapiens';
  const isVoipms = voipProvider === 'voipms';
  const voipEnabled = !!organization?.voip?.enabled;
  // Gain Rhino VoIP: which rep owns which line is set only in VoIP
  // Settings (the line list is the source of truth), so the form does not
  // ask for it and the column just mirrors it. NetSapiens keeps the
  // per-user extension field: this is the only place to set it.
  const showExtField = voipEnabled && !isVoipms;
  const extLabel = isVoipms ? 'Line' : 'Extension';
  const extPlaceholder = '101';
  const extHelp = 'NetSapiens extension. Empty = VoIP disabled.';
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { data, isLoading } = useQuery(
    'team-members',
    () => api.get('/organizations/team').then((r) => r.data),
    { onError: (err) => toast.error(err.response?.data?.error || 'Failed to load team') }
  );

  const users = data?.users || [];
  const maxUsers = data?.maxUsers || 0;
  const atLimit = maxUsers > 0 && users.length >= maxUsers;

  const createMutation = useMutation(
    (body) => api.post('/auth/register', body).then((r) => r.data),
    {
      onSuccess: () => {
        toast.success('User created');
        queryClient.invalidateQueries('team-members');
        closeModal();
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to create user')
    }
  );

  const updateMutation = useMutation(
    ({ id, patch }) => api.patch(`/organizations/users/${id}`, patch).then((r) => r.data),
    {
      onSuccess: () => {
        toast.success('User updated');
        queryClient.invalidateQueries('team-members');
        closeModal();
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to update user')
    }
  );

  const deleteMutation = useMutation(
    (id) => api.delete(`/organizations/users/${id}`).then((r) => r.data),
    {
      onSuccess: () => {
        toast.success('User deleted');
        queryClient.invalidateQueries('team-members');
        setDeleteTarget(null);
      },
      onError: (err) => {
        toast.error(err.response?.data?.error || 'Failed to delete user');
        setDeleteTarget(null);
      }
    }
  );

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (u) => {
    setForm({
      _id: u._id,
      name: u.name || '',
      email: u.email || '',
      password: '',
      phone: u.phone || '',
      role: u.role === 'admin' ? 'admin' : 'user',
      isActive: u.isActive !== false,
      extension: u.voip?.extension || ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setForm(EMPTY_FORM);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    if (!form._id && !form.password.trim()) {
      toast.error('Password is required for new users');
      return;
    }
    if (form._id) {
      const patch = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        role: form.role,
        isActive: form.isActive
      };
      if (form.password.trim()) patch.password = form.password;
      // Separate API call for the NetSapiens extension — keeps the
      // user-core patch endpoint stable and lets the server derive
      // subscriberLogin from the org's domain in one place. Only sent when
      // the field is shown and actually changed.
      const original = (users.find((m) => m._id === form._id)?.voip?.extension) || '';
      const extChanged = showExtField && form.extension.trim() !== original;
      Promise.resolve(
        extChanged
          ? api.put(`/voip/user-extension/${form._id}`, { extension: form.extension.trim() })
            .catch((err) => toast.error(err.response?.data?.error || 'Failed to save extension'))
          : null
      ).then(() => updateMutation.mutate({ id: form._id, patch }));
    } else {
      createMutation.mutate({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        phone: form.phone.trim(),
        role: form.role
      }, {
        // After the new user is created, set their extension if the
        // admin filled the field. The create endpoint doesn't accept
        // VoIP fields, so we patch them in afterwards.
        onSuccess: async (created) => {
          const ext = form.extension.trim();
          if (showExtField && ext && created?.user?._id) {
            try {
              await api.put(`/voip/user-extension/${created.user._id}`, { extension: ext });
              queryClient.invalidateQueries('team-members');
            } catch (e) {
              toast.error(e.response?.data?.error || 'User created but failed to set extension');
            }
          }
        }
      });
    }
  };

  const isSelf = (u) => String(u._id) === String(currentUser?._id);
  const saving = createMutation.isLoading || updateMutation.isLoading;

  return (
    <div className="max-w-5xl mx-auto py-6 px-2 sm:px-0">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Team Members</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage users in your organization.
            {maxUsers > 0 && (
              <span className={`ml-2 font-medium ${atLimit ? 'text-red-600' : 'text-gray-700'}`}>
                {users.length} / {maxUsers} seats used
              </span>
            )}
          </p>
        </div>
        <button
          onClick={openCreate}
          disabled={atLimit}
          title={atLimit ? 'User limit reached' : 'Add user'}
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg shadow-sm hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus size={16} className="mr-2" />
          Add user
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-10 text-center text-gray-500">Loading…</div>
        ) : users.length === 0 ? (
          <div className="p-10 text-center text-gray-500">No team members yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Phone</th>
                  {voipEnabled && (
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{extLabel}</th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {users.map((u) => (
                  <tr key={u._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div className="flex items-center">
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                          <UserIcon size={14} className="text-gray-500" />
                        </div>
                        <div>
                          <div className="font-medium">{u.name}</div>
                          {isSelf(u) && <span className="text-xs text-gray-400">You</span>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <span className="inline-flex items-center"><Mail size={14} className="mr-1.5 text-gray-400" />{u.email}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {u.phone ? (
                        <span className="inline-flex items-center"><Phone size={14} className="mr-1.5 text-gray-400" />{u.phone}</span>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                    {voipEnabled && (
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {/* VoIP.ms sub-accounts are "<master>_<name>"; a bare
                            value on a VoIP.ms org is a NetSapiens extension
                            kept for switching back, not a line. */}
                        {u.voip?.extension && (!isVoipms || u.voip.extension.includes('_')) ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-teal-50 text-teal-700 font-mono text-xs">
                            {u.voip.extension}
                          </span>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                    )}
                    <td className="px-4 py-3 text-sm">
                      {u.role === 'admin' ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                          <ShieldCheck size={12} className="mr-1" /> Admin
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">User</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {u.isActive !== false ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Active</span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">Inactive</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                      <button
                        onClick={() => openEdit(u)}
                        className="inline-flex items-center px-2 py-1 text-gray-500 hover:text-primary-700 hover:bg-primary-50 rounded transition"
                        title="Edit user"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => setDeleteTarget(u)}
                        disabled={isSelf(u)}
                        title={isSelf(u) ? "You can't delete yourself" : 'Delete user'}
                        className="inline-flex items-center px-2 py-1 text-gray-500 hover:text-red-700 hover:bg-red-50 rounded transition disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AnimatePresence>
        {showModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={saving ? undefined : closeModal}
              className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-50"
            />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden relative"
              >
                <div className="flex items-center justify-between p-5 border-b border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {form._id ? 'Edit user' : 'Add user'}
                  </h3>
                  <button onClick={closeModal} disabled={saving} className="text-gray-400 hover:text-gray-600">
                    <X size={20} />
                  </button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                      <input
                        type="text"
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                      <input
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {form._id ? 'New password' : 'Password *'}
                      </label>
                      <input
                        type="password"
                        required={!form._id}
                        minLength={6}
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        placeholder={form._id ? 'Leave blank to keep current' : 'Min. 6 characters'}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone <span className="text-xs text-gray-400">(optional)</span>
                      </label>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="+15551234567"
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                      <select
                        value={form.role}
                        onChange={(e) => setForm({ ...form, role: e.target.value })}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                      >
                        {ROLE_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                    {showExtField && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          VoIP {extLabel} <span className="text-xs text-gray-400">(optional)</span>
                        </label>
                        <input
                          type="text"
                          value={form.extension}
                          onChange={(e) => setForm({ ...form, extension: e.target.value })}
                          placeholder={extPlaceholder}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm font-mono"
                        />
                        <p className="text-xs text-gray-500 mt-1">{extHelp}</p>
                      </div>
                    )}
                    {voipEnabled && isVoipms && (
                      <p className="text-xs text-gray-500">
                        Phone lines are assigned to reps under <strong>VoIP Settings</strong>.
                      </p>
                    )}
                    {form._id && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                        <select
                          value={form.isActive ? 'active' : 'inactive'}
                          onChange={(e) => setForm({ ...form, isActive: e.target.value === 'active' })}
                          disabled={String(form._id) === String(currentUser?._id)}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm disabled:bg-gray-50"
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end space-x-2 pt-2 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={closeModal}
                      disabled={saving}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50"
                    >
                      {saving ? 'Saving…' : form._id ? 'Save changes' : 'Create user'}
                    </button>
                  </div>
                </form>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      <ConfirmationModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget._id)}
        title="Delete user"
        message={`Are you sure you want to delete ${deleteTarget?.name || 'this user'}? This action cannot be undone.`}
        confirmText="Delete"
        isDanger
        isLoading={deleteMutation.isLoading}
      />
    </div>
  );
};

export default TeamMembers;
